import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, BadgeAlert, Layers, Link2Off, RadioTower, ShieldOff } from 'lucide-react';
import StatsGrid, { Stat } from '@/client/components/common/StatsGrid';
import {
  useVirtualTourCapacityQuery,
  useVirtualTourOverviewQuery,
} from '@/client/api/listing-management/virtual-tours.query';
import { getApiErrorCode, getApiErrorMessage } from '@/client/utils/errors';
import { fmtDate, pct, StatusBadge } from './shared';

export const OverviewTab: React.FC<{ scope: string }> = ({ scope }) => {
  const { data, isLoading, isError, error } = useVirtualTourOverviewQuery(scope);
  const capacity = useVirtualTourCapacityQuery();
  const capacityUnavailable = capacity.isError && getApiErrorCode(capacity.error) === 'provider_unavailable';

  const [, setSearchParams] = useSearchParams();
  // Jump to the tab a tile/alert describes (VirtualToursPage reads `?tab=`; filters are `<ns>_<key>`).
  const go = (tab: string, extra: Record<string, string> = {}) => setSearchParams({ tab, ...extra });
  const goErrors = () => go('errors', { err_outcome: 'error', err_hours: '24' });

  const byStatus = data?.by_status ?? {};
  const statusStats: Stat[] = Object.entries(byStatus).map(([status, n]) => ({
    key: status,
    label: status.replace(/_/g, ' '),
    value: n,
  }));

  const utilisation = capacity.data?.utilisation;
  const alerts: { text: string; onClick: () => void }[] = [];
  if ((data?.errors_24h ?? 0) > 0) alerts.push({ text: `${data?.errors_24h} sync error(s) in the last 24h.`, onClick: goErrors });
  if ((data?.linked_without_asset ?? 0) > 0)
    alerts.push({ text: `${data?.linked_without_asset} listing(s) point at a tour with no linked asset.`, onClick: () => go('sync') });
  if ((data?.without_eligible_plan ?? 0) > 0)
    alerts.push({
      text: `${data?.without_eligible_plan} tour(s) are shown on listings with no plan or promotion that includes a virtual tour.`,
      onClick: () => go('tours', { tours_eligibility: 'ineligible' }),
    });
  if (utilisation != null && utilisation >= 0.9) alerts.push({ text: `Matterport capacity at ${pct(utilisation)} — nearing the cap.`, onClick: () => go('capacity') });

  return (
    <div className="space-y-6">
      {isError && (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {getApiErrorMessage(error, 'Could not load the overview.')}
        </div>
      )}

      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <button
              key={i}
              onClick={a.onClick}
              className="flex w-full items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800 hover:bg-amber-100"
            >
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {a.text}
            </button>
          ))}
        </div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700">Tours by status</h3>
        <StatsGrid
          stats={
            statusStats.length
              ? statusStats
              : [{ key: 'none', label: 'No tours yet', value: 0 }]
          }
          isLoading={isLoading}
          onStatClick={(key) => key in byStatus && go('tours', { tours_status: key })}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button onClick={goErrors} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            <AlertTriangle className="h-4 w-4" /> Errors (24h)
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{data?.errors_24h ?? '—'}</p>
        </button>
        <button onClick={() => go('capacity')} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            <RadioTower className="h-4 w-4" /> Active on non-live listings
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{data?.active_on_non_live_listings ?? '—'}</p>
        </button>
        <button onClick={() => go('sync')} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            <Link2Off className="h-4 w-4" /> Linked without asset
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{data?.linked_without_asset ?? '—'}</p>
        </button>
        <button onClick={() => go('capacity')} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            <Layers className="h-4 w-4" /> Capacity utilisation
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900">
            {capacityUnavailable ? '—' : pct(utilisation)}
          </p>
          {capacityUnavailable && <p className="mt-1 text-xs text-amber-600">Matterport unreachable</p>}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button onClick={() => go('tours', { tours_eligibility: 'ineligible' })} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            <BadgeAlert className="h-4 w-4" /> No eligible plan
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{data?.without_eligible_plan ?? '—'}</p>
        </button>
        <button onClick={() => go('tours', { tours_source: 'admin_bypass' })} className="rounded-lg border border-gray-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-gray-50">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            <ShieldOff className="h-4 w-4" /> Linked by bypass
          </div>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{data?.linked_by_bypass ?? '—'}</p>
        </button>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-gray-700">Last reconcile run</h3>
        {data?.last_run ? (
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <span className="text-xs text-gray-400">Status</span>
              <div className="mt-1"><StatusBadge status={data.last_run.status} /></div>
            </div>
            <div>
              <span className="text-xs text-gray-400">Finished</span>
              <p className="font-medium">{fmtDate(data.last_run.finished_at)}</p>
            </div>
            <div>
              <span className="text-xs text-gray-400">Linked / Blocked</span>
              <p className="font-medium">
                {data.last_run.linked ?? 0} / {data.last_run.quota_blocked ?? 0}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-400">Failed</span>
              <p className="font-medium">{data.last_run.failed ?? 0}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No reconcile has run yet.</p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="mb-2 text-sm font-semibold text-gray-700">Providers</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(data?.providers ?? {}).map(([key, p]) => (
            <span
              key={key}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-700"
            >
              {p.label}
              {!p.api && <span className="text-gray-400">(no API)</span>}
            </span>
          ))}
          {!data?.providers && !isLoading && <span className="text-sm text-gray-500">No providers configured.</span>}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
